import React, { useState, useMemo } from 'react';
import { useAppContext } from '../context/AppContext';
import { 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  format, 
  isSameDay, 
  addMonths, 
  subMonths, 
  startOfWeek, 
  endOfWeek, 
  isSameMonth 
} from 'date-fns';
import { getOccurrencesInMonth, formatDate } from '../utils/dateUtils';
import SafeIcon from '../common/SafeIcon';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Forms';

export default function CalendarView() {
  const { state } = useAppContext();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // Restored status filter

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const events = useMemo(() => {
    const evts = [];
    
    // Apply Filters
    const filteredSubs = state.subscriptions.filter(sub => {
      const matchCat = categoryFilter === 'all' || sub.categoryId === categoryFilter;
      const matchStatus = statusFilter === 'all' || (sub.status || 'active') === statusFilter;
      return matchCat && matchStatus;
    });

    filteredSubs.forEach(sub => {
      // Show recurring billing dates
      const billingDates = getOccurrencesInMonth(currentMonth, sub.commencementDate, sub.recurrenceInterval, sub.cancelledAt);
      billingDates.forEach(date => {
        evts.push({ date, type: 'billing', sub });
      });

      // Show recurring credit reset dates
      if (sub.aiCreditEnabled) {
        const creditDates = getOccurrencesInMonth(currentMonth, sub.commencementDate, sub.aiCreditRecurrenceInterval, sub.cancelledAt);
        creditDates.forEach(date => {
          evts.push({ date, type: 'credit', sub });
        });
      }
    });
    return evts;
  }, [state.subscriptions, currentMonth, categoryFilter, statusFilter]);

  const getEventsForDay = (day) => events.filter(e => isSameDay(e.date, day));

  const changeMonth = (offset) => {
    setCurrentMonth(prev => offset > 0 ? addMonths(prev, 1) : subMonths(prev, 1));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Calendar</h1>
          <p className="text-slate-500">View recurring payments across any month.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Restored Status Selector */}
          <div className="relative min-w-[140px]">
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-teal-500 appearance-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="cancelled">Cancelled Only</option>
            </select>
            <SafeIcon name="ChevronDown" className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>

          <div className="relative min-w-[160px]">
            <select 
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-teal-500 appearance-none"
            >
              <option value="all">All Categories</option>
              {state.categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <SafeIcon name="ChevronDown" className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>

          <div className="flex items-center bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <button onClick={() => changeMonth(-1)} className="p-2.5 hover:bg-slate-50 border-r border-slate-100 transition-colors">
              <SafeIcon name="ChevronLeft" className="w-5 h-5" />
            </button>
            <div className="px-6 py-2.5 font-bold text-slate-800 min-w-[150px] text-center">
              {format(currentMonth, 'MMMM yyyy')}
            </div>
            <button onClick={() => changeMonth(1)} className="p-2.5 hover:bg-slate-50 border-l border-slate-100 transition-colors">
              <SafeIcon name="ChevronRight" className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/50">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 auto-rows-fr">
          {calendarDays.map((day) => {
            const dayEvents = getEventsForDay(day);
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = isSameMonth(day, currentMonth);
            
            return (
              <div key={day.toISOString()} className={`min-h-[120px] p-2 border-b border-r border-slate-100 ${!isCurrentMonth ? 'bg-slate-50/50 opacity-40' : ''} ${isToday ? 'bg-teal-50/30' : 'hover:bg-slate-50/30'}`}>
                <div className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full mb-2 ${isToday ? 'bg-teal-500 text-white' : 'text-slate-600'}`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-1.5">
                  {dayEvents.map((evt, idx) => (
                    <button 
                      key={idx} 
                      onClick={() => setSelectedEvent(evt)}
                      className={`w-full text-left text-[10px] md:text-xs px-2 py-1.5 rounded-md truncate font-medium flex items-center gap-1.5 transition-all hover:scale-[1.02] ${evt.sub.status === 'cancelled' ? 'opacity-60 grayscale' : ''} ${evt.type === 'billing' ? 'bg-rose-50 text-rose-700 border border-rose-100' : 'bg-purple-50 text-purple-700 border border-purple-100'}`}
                    >
                      <SafeIcon name={evt.type === 'billing' ? 'DollarSign' : 'Cpu'} className="w-3 h-3 flex-shrink-0" />
                      {evt.sub.name}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Modal isOpen={!!selectedEvent} onClose={() => setSelectedEvent(null)} title={selectedEvent?.type === 'billing' ? 'Subscription Payment' : 'AI Credits Reset'}>
        {selectedEvent && (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              {selectedEvent.sub.logo ? (
                <img src={selectedEvent.sub.logo} alt="" className="w-16 h-16 rounded-2xl object-cover bg-slate-50" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center">
                  <SafeIcon name={state.categories.find(c => c.id === selectedEvent.sub.categoryId)?.icon || 'Tag'} className="w-8 h-8" />
                </div>
              )}
              <div>
                <h3 className="text-xl font-bold text-slate-800">{selectedEvent.sub.name}</h3>
                <p className="text-slate-500">{selectedEvent.sub.planName || 'Standard Plan'}</p>
                {selectedEvent.sub.status === 'cancelled' && (
                  <span className="inline-block mt-1 px-2 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-bold uppercase border border-slate-200">
                    Cancelled
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-xs text-slate-400 uppercase font-bold mb-1">
                  {selectedEvent.type === 'billing' ? 'Payment Date' : 'Reset Date'}
                </p>
                <p className="font-semibold text-slate-700">{formatDate(selectedEvent.date)}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-xs text-slate-400 uppercase font-bold mb-1">
                  {selectedEvent.type === 'billing' ? 'Amount' : 'Credits'}
                </p>
                <p className="font-semibold text-slate-700">
                  {selectedEvent.type === 'billing' ? `$${selectedEvent.sub.recurringAmount.toFixed(2)}` : `${selectedEvent.sub.aiCreditAmount} units`}
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={() => setSelectedEvent(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}